import React from 'react';
import { cn } from '../../utils/cn';
import { StatusChip } from '../ui/StatusChip';
import { ChaseBadge, ChaseKind } from '../positions/ChaseBadge';
import CappedChip from '../positions/CappedChip';
import type { SpotRow } from '../../utils/ulSpotLevels';

/**
 * Shared compact ISO→IST timestamp formatter used in LegSection footers.
 * Format: `DD MMM YYYY · HH:MM:SS` (e.g. `23 Apr 2026 · 14:23:11`).
 */
export function formatLegTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const time = d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false });
    const date = d.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
    });
    return `${date} · ${time}`;
  } catch {
    return '';
  }
}

export type LegPrices = {
  trigger?: number | null;
  expected?: number | null;
  limit?: number | null;
  filled?: number | null;
};

export interface LegSectionProps {
  title: 'Entry' | 'Stop-Loss' | 'Exit';
  prices: LegPrices;
  timestamp?: string | null;
  status?: string | null;
  errorMessage?: string | null;
  /** E.g. "2 attempts" — rendered in the footer next to the timestamp. */
  extra?: string;
  /**
   * Optional "Type" row rendered between the price grid and the footer
   * timestamp. Used by the Exit section to surface the exit reason (SL / MANUAL
   * / EOD …) as a labeled field instead of burying it inline in `extra`.
   */
  typeLabel?: string | null;
  /** Fallback hint when no concrete prices exist yet (e.g. SL config "POINTS 10"). */
  configHint?: string;
  /**
   * Index-spot levels of an underlying-based (UL) leg — entry spot, spot SL /
   * target, exit spot — rendered below the premium prices (which stay as they
   * are), each with how it was derived. See utils/ulSpotLevels.
   */
  spot?: SpotRow[];
  /** Compact formatter for ISO timestamps. Supply the same helper in each call site so the look stays uniform. */
  formatTimestamp?: (iso: string) => string;
  /**
   * `'card'` — padded, stacked, suitable for a flex/grid cell (Pro modal leg card).
   * `'cell'` — tighter padding, no borders, suitable for a `<td>` (Classic table row).
   */
  variant?: 'card' | 'cell';
  /**
   * Optional chase indicator rendered in the header alongside the StatusChip.
   * Pass `{ kind, count: 0 }` and the badge renders nothing — so callers can
   * unconditionally pass the prop and let the badge gate itself. Currently
   * wired in by StrategyPositionsModalV2 (Pro view); Classic (StrategyPositionsModal) omits it
   * so the chip never appears there.
   */
  chase?: {
    kind: ChaseKind;
    count: number;
    initialLimit?: number | null;
    finalLimit?: number | null;
    fillPrice?: number | null;
  };
  /**
   * Optional "capped" indicator: when set, renders an amber `CHIP_RISK.CAPPED`
   * chip in the header next to the StatusChip. Used by the SL and Entry cells
   * of `StrategyPositionsModal` when the broker's daily circuit limit forced
   * the placed trigger below the user's configured (desired) level.
   *
   * The chip's tooltip shows both values so the user knows exactly what was
   * capped and what the recovery target is. Recovery is automatic — Phase 2/3
   * SOD re-placements attempt the canonical level as bands widen.
   *
   * Pass `null`/`undefined` when not capped — the chip then renders nothing.
   */
  capped?: {
    placed: number | null | undefined;
    desired: number | null | undefined;
  } | null;
}

/** One labeled price row inside a LegSection; hides itself when value is null. */
export const PriceRow: React.FC<{
  label: string;
  value: number | null | undefined;
  highlight?: boolean;
}> = ({ label, value, highlight }) => {
  if (value == null) return null;
  return (
    <div className="flex items-center justify-between gap-2 text-xs tabular-nums leading-tight">
      <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <span className={cn(
        'font-semibold',
        highlight ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200',
      )}>
        {value.toFixed(2)}
      </span>
    </div>
  );
};

/**
 * Shared per-leg Entry / Stop-Loss / Exit section.
 *
 * Renders a header (title + status chip), a price grid (Trigger/Expected/Limit/Filled
 * rows that auto-hide when null), index-spot rows for UL legs, and a footer
 * (timestamp + extra). Used by the Classic strategy modal (inside a `<td>` via
 * `variant="cell"`).
 */
export const LegSection: React.FC<LegSectionProps> = ({
  title,
  prices,
  timestamp,
  status,
  errorMessage,
  extra,
  typeLabel,
  configHint,
  spot,
  formatTimestamp,
  variant = 'card',
  chase,
  capped,
}) => {
  // Map the LegSection's `title` to the CappedChip's `kind`. CappedChip only
  // accepts 'Stop-Loss' | 'Entry' for tooltip wording — the Exit cell shouldn't
  // ever be capped (no broker trigger), so we map it to 'Stop-Loss' defensively
  // but the gate at the call site means capped is null on Exit anyway.
  const cappedKind: 'Stop-Loss' | 'Entry' =
    title === 'Entry' ? 'Entry' : 'Stop-Loss';
  const hasAnyPrice =
    prices.trigger != null
    || prices.expected != null
    || prices.limit != null
    || prices.filled != null;

  return (
    <div className={cn(
      variant === 'card' ? 'p-3 space-y-1.5' : 'space-y-1',
    )}>
      {/* Header: title + chase chip (Pro only — Classic omits the prop) +
          capped chip (Phase 2/3 — circuit-cap recovery) + status chip */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{title}</span>
        <div className="flex items-center gap-1 shrink-0">
          {chase && (
            <ChaseBadge
              kind={chase.kind}
              count={chase.count}
              initialLimit={chase.initialLimit}
              finalLimit={chase.finalLimit}
              fillPrice={chase.fillPrice}
            />
          )}
          {capped && (
            <CappedChip
              placed={capped.placed}
              desired={capped.desired}
              kind={cappedKind}
              testId={`leg-section-capped-${title.toLowerCase()}`}
            />
          )}
          <StatusChip status={status} size="xs" />
        </div>
      </div>

      {/* Price grid */}
      {hasAnyPrice ? (
        <div className="space-y-0.5">
          <PriceRow label="Trigger"  value={prices.trigger} />
          <PriceRow label="Expected" value={prices.expected} />
          <PriceRow label="Limit"    value={prices.limit} />
          <PriceRow label="Filled"   value={prices.filled} highlight />
        </div>
      ) : configHint ? (
        <div className="text-[10px] italic text-gray-500 dark:text-gray-400">{configHint}</div>
      ) : !spot?.length && (
        <div className="text-xs text-gray-300 dark:text-gray-600">—</div>
      )}

      {/* Index-spot levels (UL legs) */}
      {!!spot?.length && (
        <div className="space-y-0.5">
          {spot.map((row) => (
            <div key={row.label}>
              {/* Not highlighted: an index level must not read like the bold
                  premium "Filled" price above it. */}
              <PriceRow label={row.label} value={row.value} />
              {row.note && (
                <div className="text-[10px] leading-tight text-gray-500 dark:text-gray-400">{row.note}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Type row — rendered between prices and footer so it reads as a
          labeled field (e.g. "Type · SL") rather than a throwaway footnote. */}
      {typeLabel && (
        <div className="flex items-center justify-between gap-2 text-xs leading-tight">
          <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</span>
          <span className="font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider text-[11px]">
            {typeLabel}
          </span>
        </div>
      )}

      {/* Footer meta */}
      {(timestamp || extra) && (
        <div className="flex items-center gap-2 flex-wrap text-[10px] text-gray-500 dark:text-gray-400">
          {timestamp && (
            <span className="tabular-nums">
              {formatTimestamp ? formatTimestamp(timestamp) : timestamp}
            </span>
          )}
          {extra && <span className="text-gray-400 dark:text-gray-500">{extra}</span>}
        </div>
      )}

      {errorMessage && (
        <div className="text-[10px] text-red-500 dark:text-red-400 break-words">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default LegSection;
